import { computed, defineComponent, unref, type FunctionalComponent, type MaybeRef } from 'vue';
import Icon from '../icon';
import type { IconFactor } from '@/types/elements/icon';
import type { JSX } from 'vue/jsx-runtime';
import { useResponsive } from '~/composables/useResponsive';

export const DetailCard = defineComponent<{
  title: string;
  subtitle: string;
  badgeText: string;
  badgeMaintained: {
    name?: string;
    iconUrl?: string;
    title: string;
  };
  onClose?: () => any;
  icon?: IconFactor;
  iconBody?: FunctionalComponent<any>;
  iconContent?: MaybeRef<JSX.Element>;
  closeIsDisabled?: MaybeRef<boolean>;
}>(
  (props) => {
    const responsive = useResponsive();
    const handleClose = () => {
      if (props.onClose) {
        props.onClose();
      }
    };

    const iconContent = computed(() => unref(props.iconContent));
    const closeIsDisabled = computed(() => !!unref(props.closeIsDisabled));

    return () => {
      return (
        <div class="w-full md:w-auto h-16 px-4 py-3 rounded-xs border border-gray-300 grid grid-cols-[max-content_max-content] items-center justify-between select-none">
          <div class="h-10 justify-start items-center gap-2.5 flex">
            <div class="w-10 h-10 bg-gray-100 justify-center items-center flex">
              <div class="text-gray-400">
                {iconContent.value ? (
                  iconContent.value
                ) : props.iconBody ? (
                  <props.iconBody />
                ) : props.icon ? (
                  <Icon src={props.icon} />
                ) : (
                  ''
                )}
              </div>
            </div>
            <div class="flex-col justify-start items-start inline-flex overflow-hidden">
              <div class="text-[#111928] text-xs font-semibold font-['Inter'] leading-[18px]">
                {props.title}
              </div>
              <div class="text-gray-500 text-xs font-normal font-['Inter'] leading-[18px]">
                {props.subtitle}
              </div>
            </div>
          </div>
          <div class="grid grid-cols-[max-content_max-content_max-content] items-center justify-end gap-2">
            {!responsive.isPhone.value &&
              <div class="px-2.5 py-1 bg-gray-100 rounded-xs justify-center items-center flex">
                <div class="text-center text-gray-500 text-xs font-semibold font-['Inter'] leading-[18px]">
                  {props.badgeText}
                </div>
              </div>
            }
            {!responsive.isPhone.value &&
              <div class="px-2.5 py-1 bg-gray-100 rounded-xs justify-center items-center flex">
                <div class="text-center text-gray-500 text-xs font-semibold font-['Inter'] leading-[18px]">
                  <div class="flex items-center gap-1">
                    {props.badgeMaintained?.iconUrl && (
                      <img
                        alt={props.badgeMaintained?.name ?? 'provider'}
                        class="h-3.5 border border-gray-300 object-cover"
                        src={props.badgeMaintained?.iconUrl}
                      />
                    )}
                    <div class="text-center text-gray-500 text-xs font-semibold font-['Inter'] leading-[18px] capitalize">
                      {props.badgeMaintained?.title}
                    </div>
                  </div>
                </div>
              </div>
            }
            <div
              class={[
                'text-gray-400 transition-colors',
                closeIsDisabled.value ? 'opacity-20' : 'cursor-pointer hover:text-gray-300',
              ]}
              onClick={closeIsDisabled.value ? undefined : handleClose}
            >
              <Icon
                src={async () => import('assets/img/svg/icons/close2.svg?raw')}
                color={'currentColor'}
                size="0.86rem"
                stroke={'currentColor'}
              />
            </div>
          </div>
        </div>
      );
    };
  },
  {
    props: [
      'title',
      'subtitle',
      'badgeText',
      'badgeMaintained',
      'onClose',
      'icon',
      'iconBody',
      'iconContent',
      'closeIsDisabled',
    ],
  },
);
